import GPTTokenizer from 'gpt-tokenizer';

const PRICING_SOURCES_KEY = 'pricing.sources.json';
const PRICING_CACHE_KEY = 'pricing.cache.json';

const DEFAULT_PRICING_SOURCES = [
  {
    id: 'litellm_github',
    label: 'LiteLLM (GitHub JSON)',
    url: 'https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json',
    priority: 1,
    last_checked_iso: null
  }
];

// Fallback-Preise (USD pro 1M Tokens). Diese Werte werden genutzt, wenn kein Cache verfügbar ist.
export const TOKEN_COSTS = {
  'gpt-4': { input: 30, output: 60 },
  'gpt-4-turbo': { input: 10, output: 30 },
  'gpt-4o': { input: 5, output: 15 },
  'gpt-4o-mini': { input: 0.15, output: 0.60 },
  'gpt-3.5-turbo': { input: 0.5, output: 1.5 },
  'claude-3-opus': { input: 15, output: 75 },
  'claude-3-sonnet': { input: 3, output: 15 },
  'claude-3-haiku': { input: 0.25, output: 1.25 },
  'claude-3.5-sonnet': { input: 3, output: 15 },
  'gemini-pro': { input: 0, output: 0 },
  'gemini-1.5-pro': { input: 0, output: 0 },
  'gemini-1.5-flash': { input: 0, output: 0 },
  'gemini-ultra': { input: 0, output: 0 }
};

const hasLocalStorage = () => {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
};

const safeJsonParse = (value, fallback = null) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const nowIso = () => new Date().toISOString();

const hoursBetween = (isoA, isoB) => {
  try {
    const a = new Date(isoA).getTime();
    const b = new Date(isoB).getTime();
    return Math.abs(b - a) / (1000 * 60 * 60);
  } catch {
    return null;
  }
};

const daysBetween = (isoA, isoB) => {
  const h = hoursBetween(isoA, isoB);
  if (h === null) return null;
  return h / 24;
};

export const loadPricingSources = () => {
  if (!hasLocalStorage()) return DEFAULT_PRICING_SOURCES;
  const raw = localStorage.getItem(PRICING_SOURCES_KEY);
  const parsed = safeJsonParse(raw, null);
  if (Array.isArray(parsed) && parsed.length) return parsed;
  return DEFAULT_PRICING_SOURCES;
};

export const savePricingSources = (sources) => {
  try {
    if (!hasLocalStorage()) return;
    localStorage.setItem(PRICING_SOURCES_KEY, JSON.stringify(sources, null, 2));
  } catch {
    // still safe without persistence
  }
};

export const loadPricingCache = () => {
  if (!hasLocalStorage()) return null;
  const raw = localStorage.getItem(PRICING_CACHE_KEY);
  const parsed = safeJsonParse(raw, null);
  if (parsed && typeof parsed === 'object' && parsed.models) return parsed;
  return null;
};

export const savePricingCache = (cache) => {
  try {
    if (!hasLocalStorage()) return;
    localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify(cache, null, 2));
  } catch {
    // still safe without persistence
  }
};

export const getPricingCacheMeta = (cache) => {
  const updated = cache?.updated_at_iso || null;
  const ageHours = updated ? hoursBetween(updated, nowIso()) : null;
  const ageDays = updated ? daysBetween(updated, nowIso()) : null;

  return {
    updated_at_iso: updated,
    age_hours: ageHours,
    age_days: ageDays,
    is_older_than_24h: typeof ageHours === 'number' ? ageHours > 24 : true,
    is_older_than_7d: typeof ageDays === 'number' ? ageDays > 7 : true
  };
};

const normalizeLitellm = (rawJson) => {
  const out = {
    schema_version: '1',
    updated_at_iso: nowIso(),
    sources: [],
    models: {}
  };

  if (!rawJson || typeof rawJson !== 'object') return out;

  Object.entries(rawJson).forEach(([modelId, modelData]) => {
    if (!modelId || !modelData || typeof modelData !== 'object') return;

    // LiteLLM nutzt je nach Version unterschiedliche Feldnamen.
    const provider =
      modelData.litellm_provider ||
      modelData.provider ||
      modelData.provider_name ||
      'Unbekannt';

    const inputCostPerToken =
      modelData.input_cost_per_token ??
      modelData.input_cost ??
      modelData.input_cost_per_unit ??
      null;

    const outputCostPerToken =
      modelData.output_cost_per_token ??
      modelData.output_cost ??
      modelData.output_cost_per_unit ??
      null;

    const inputPer1M =
      typeof inputCostPerToken === 'number'
        ? (inputCostPerToken > 1
            ? Math.round(inputCostPerToken * 1000000) / 1000000
            : Math.round(inputCostPerToken * 1000000 * 1000000) / 1000000)
        : null;

    const outputPer1M =
      typeof outputCostPerToken === 'number'
        ? (outputCostPerToken > 1
            ? Math.round(outputCostPerToken * 1000000) / 1000000
            : Math.round(outputCostPerToken * 1000000 * 1000000) / 1000000)
        : null;

    const contextWindow =
      modelData.max_input_tokens ??
      modelData.max_tokens ??
      modelData.context_window ??
      modelData.context_window_tokens ??
      null;

    const maxOutput =
      modelData.max_output_tokens ??
      modelData.max_completion_tokens ??
      null;

    out.models[modelId] = {
      id: modelId,
      provider,
      input_per_1m_usd: inputPer1M,
      output_per_1m_usd: outputPer1M,
      context_window_tokens: typeof contextWindow === 'number' ? contextWindow : null,
      max_output_tokens: typeof maxOutput === 'number' ? maxOutput : null,
      source: 'litellm_github'
    };
  });

  return out;
};

const fetchJsonWithTimeout = async (url, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const resp = await fetch(url, { signal: controller.signal, cache: 'no-store' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } finally {
    clearTimeout(timer);
  }
};

const refreshPricingCache = async (sources) => {
  const now = nowIso();

  const sorted = [...sources].sort((a, b) => (a.priority || 99) - (b.priority || 99));
  let lastError = null;

  for (const src of sorted) {
    try {
      const raw = await fetchJsonWithTimeout(src.url);
      const normalized = normalizeLitellm(raw);

      const updatedSources = sources.map(s => {
        if (s.id !== src.id) return s;
        return { ...s, last_checked_iso: now };
      });

      const cache = {
        ...normalized,
        updated_at_iso: now,
        sources: updatedSources
      };

      savePricingSources(updatedSources);
      savePricingCache(cache);
      return cache;
    } catch (e) {
      lastError = e;
      continue;
    }
  }

  // Wenn alles fehlschlaegt: Cache unveraendert lassen.
  const existing = loadPricingCache();
  if (existing) return existing;

  return {
    schema_version: '1',
    updated_at_iso: null,
    sources: sources,
    models: {},
    error: lastError ? String(lastError) : 'unknown_error'
  };
};

export const ensurePricingCacheFresh = async ({ force = false } = {}) => {
  const sources = loadPricingSources();
  const cached = loadPricingCache();
  const meta = getPricingCacheMeta(cached);

  // Falls kein Cache existiert, muessen wir aktualisieren.
  if (!cached) {
    return await refreshPricingCache(sources);
  }

  // Force-Refresh oder aelter als 24h => Update.
  if (force || meta.is_older_than_24h) {
    return await refreshPricingCache(sources);
  }

  return cached;
};

export const getPricingForModel = (modelId) => {
  const cache = loadPricingCache();
  const cachedModel = cache?.models?.[modelId] || null;

  if (cachedModel) {
    return {
      input_per_1m_usd: cachedModel.input_per_1m_usd,
      output_per_1m_usd: cachedModel.output_per_1m_usd,
      context_window_tokens: cachedModel.context_window_tokens,
      max_output_tokens: cachedModel.max_output_tokens,
      provider: cachedModel.provider,
      source: cachedModel.source || 'cache'
    };
  }

  const fallback = TOKEN_COSTS[modelId] || null;

  return {
    input_per_1m_usd: fallback ? fallback.input : null,
    output_per_1m_usd: fallback ? fallback.output : null,
    context_window_tokens: null,
    max_output_tokens: null,
    provider: null,
    source: 'fallback'
  };
};

export const getModelOptions = ({ includeFallback = true } = {}) => {
  const cache = loadPricingCache();
  const cachedIds = cache?.models ? Object.keys(cache.models) : [];
  const options = cachedIds.map(id => {
    const m = cache.models[id];
    const provider = m?.provider ? String(m.provider) : 'Unbekannt';
    return {
      id,
      label: `${id} (${provider})`,
      provider
    };
  });

  // Wenn kein Cache oder Cache leer: Fallback-Optionen
  if (!options.length && includeFallback) {
    return [
      { id: 'gpt-4', label: 'gpt-4 (OpenAI)', provider: 'OpenAI' },
      { id: 'gpt-4-turbo', label: 'gpt-4-turbo (OpenAI)', provider: 'OpenAI' },
      { id: 'gpt-4o', label: 'gpt-4o (OpenAI)', provider: 'OpenAI' },
      { id: 'gpt-4o-mini', label: 'gpt-4o-mini (OpenAI)', provider: 'OpenAI' },
      { id: 'gpt-3.5-turbo', label: 'gpt-3.5-turbo (OpenAI)', provider: 'OpenAI' },
      { id: 'claude-3-opus', label: 'claude-3-opus (Anthropic)', provider: 'Anthropic' },
      { id: 'claude-3-sonnet', label: 'claude-3-sonnet (Anthropic)', provider: 'Anthropic' },
      { id: 'claude-3-haiku', label: 'claude-3-haiku (Anthropic)', provider: 'Anthropic' },
      { id: 'claude-3.5-sonnet', label: 'claude-3.5-sonnet (Anthropic)', provider: 'Anthropic' },
      { id: 'gemini-pro', label: 'gemini-pro (Google)', provider: 'Google' },
      { id: 'gemini-1.5-pro', label: 'gemini-1.5-pro (Google)', provider: 'Google' },
      { id: 'gemini-1.5-flash', label: 'gemini-1.5-flash (Google)', provider: 'Google' },
      { id: 'gemini-ultra', label: 'gemini-ultra (Google)', provider: 'Google' }
    ];
  }

  // sort: provider -> id
  options.sort((a, b) => {
    const pa = a.provider || '';
    const pb = b.provider || '';
    if (pa !== pb) return pa.localeCompare(pb);
    return a.id.localeCompare(b.id);
  });

  return options;
};

export const estimateTokens = (text, model = 'gpt-4') => {
  try {
    if (model === 'gpt-4' || model === 'gpt-4-turbo' || model === 'gpt-4o' || model === 'gpt-4o-mini' || model === 'gpt-3.5-turbo') {
      return GPTTokenizer.encode(text).length;
    } else if (model.startsWith('claude-3') || model.startsWith('claude-3.5')) {
      return Math.ceil(text.length / 4);
    } else if (model.startsWith('gemini')) {
      return Math.ceil(text.length / 4);
    }
    return Math.ceil(text.length / 4);
  } catch (error) {
    return Math.ceil(text.length / 4);
  }
};

export const estimateCost = (tokens, model = 'gpt-4', type = 'input') => {
  const pricing = getPricingForModel(model);
  const costPer1M = type === 'output' ? pricing.output_per_1m_usd : pricing.input_per_1m_usd;
  const cost = typeof costPer1M === 'number' ? costPer1M : 0;
  return (tokens * cost) / 1000000;
};

export const formatTokenCount = (tokens) => {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(2)}M`;
  } else if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}k`;
  }
  return tokens.toString();
};